import { Link } from 'react-router-dom';

import { PageHeader } from '@/components/common/PageHeader';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import {
  OrganizationGrid,
  OrganizationGridSkeleton,
} from '@/features/orgs/components/OrganizationGrid';
import { useGetOrganizationsQuery } from '@/features/orgs/api/orgApi';

/**
 * Dashboard landing view (`/`) inside the protected app shell (design §8).
 *
 * P1-T4 shipped the header/copy of this page; P1-T5 fills the one section the
 * design allows — the user's organizations as cards, from `GET /organizations`
 * (the same query the organizations list uses, so both views share one cache
 * entry). No per-card counts: that would need N detail calls (rejected, G3), and
 * no greeting: nothing in the JWT guarantees a name (§8).
 *
 * “View all organizations” appears only past six orgs (progressive, G1).
 */
export function DashboardPage() {
  const { data, isLoading, isError, error, refetch } = useGetOrganizationsQuery();
  const organizations = data ?? [];

  return (
    <section aria-labelledby="dashboard-title">
      <PageHeader
        title="Home"
        titleId="dashboard-title"
        description="Your organizations and projects at a glance."
      />

      <div className="mb-3 flex items-center justify-between gap-4">
        <h2 id="dashboard-organizations" className="text-lg font-semibold">
          Organizations
        </h2>
        {organizations.length > 6 ? (
          <Link
            to="/organizations"
            className="shrink-0 text-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
          >
            View all organizations →
          </Link>
        ) : null}
      </div>

      {isLoading ? (
        <OrganizationGridSkeleton />
      ) : isError ? (
        <QueryErrorView error={error} label="organizations" onRetry={() => void refetch()} />
      ) : (
        <OrganizationGrid
          organizations={organizations}
          emptyTitle="You're not in any organizations yet"
        />
      )}
    </section>
  );
}
